import { useState } from 'react';
import type { UIMenuSection as UIMenuSectionTypes } from '@creoate/virgo-content-api';

// mui
import Box from '@mui/material/Box';
import Grid2 from '@mui/material/Grid2';

// hooks
import useVirgoContent from 'hooks/useVirgoContent';

// components
import CategoryLink from './CategoryLink';
import BannersImage from '../components/BannersImage';
import MenuTitle from '../components/MenuTitle';

// utils
import { TopMenuPopovers } from '../utils/types';

const UIMenuSection = ({ hoveredNavLinkItem }: TopMenuPopovers) => {
  const { topMenu } = useVirgoContent();

  const [selectedCategory, setSelectedCategory] = useState<number>(0);

  const setSelectedStates = (a: number) => setSelectedCategory(a);

  const selectedMenu = topMenu?.filter(
    (menu, index) => index === hoveredNavLinkItem?.index && menu?.component === hoveredNavLinkItem?.component
  )?.[0] as UIMenuSectionTypes;

  return (
    <Grid2
      container
      width="100%"
      spacing="20px"
      sx={{ overflow: 'hidden' }}
      key={`ui.menu-section-${hoveredNavLinkItem?.index}`}
      data-testid={`desktop-top-menu-content-${hoveredNavLinkItem?.component}-${hoveredNavLinkItem?.label}`}
    >
      {/* categories */}
      <Grid2
        size={{ xs: 12, md: 3 }}
        sx={{
          height: '100%',
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '7px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#9E9E9E',
            borderRadius: '4px',
          },
        }}
        key={`ui.menu-section-${hoveredNavLinkItem?.index}-categories`}
      >
        {selectedMenu?.categories?.length > 0 && (
          <>
            <CategoryLink label={`Shop All ${hoveredNavLinkItem?.label}`} href={selectedMenu?.link?.url} type="title" />

            {selectedMenu?.categories?.map((category, index) => (
              <CategoryLink
                label={category?.name}
                isActive={index === selectedCategory}
                onMouseEnter={() => setSelectedStates(index)}
                showRightIcon={category?.subcategories?.length > 0}
                href={`/product-category/${category?.slug}`}
                key={`ui.menu-section-${selectedMenu?.label}-category-${index + 1}`}
              />
            ))}
          </>
        )}
      </Grid2>

      {/* sub categories */}
      <Grid2
        size={{ xs: 12, md: 3 }}
        sx={{
          height: '100%',
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '7px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#9E9E9E',
            borderRadius: '4px',
          },
        }}
        key={`ui.menu-section-${selectedMenu?.label}-subcategories`}
      >
        {selectedMenu?.categories?.[selectedCategory]?.subcategories?.length > 0 && (
          <>
            <CategoryLink
              type="title"
              label={`Shop All ${selectedMenu?.categories?.[selectedCategory]?.name}`}
              href={`/product-category/${selectedMenu?.categories?.[selectedCategory]?.slug}`}
            />

            {selectedMenu?.categories?.[selectedCategory]?.subcategories?.map((category, index) => (
              <CategoryLink
                label={category?.name}
                href={`/product-category/${category?.slug}`}
                key={`ui.menu-section-${selectedMenu?.label}-subcategory-${index + 1}`}
              />
            ))}
          </>
        )}
      </Grid2>

      {/* wholesalers */}
      <Grid2
        size={{ xs: 12, md: 3 }}
        sx={{
          height: '100%',
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '7px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#9E9E9E',
            borderRadius: '4px',
          },
        }}
        key={`ui.menu-section-${hoveredNavLinkItem?.index}-wholesalers`}
      >
        {selectedMenu?.wholesalers?.length > 0 && (
          <>
            <MenuTitle title="Featured Brands" />

            <Box sx={{ overflowY: 'auto' }}>
              {selectedMenu?.wholesalers?.map((wholesaler, index) => (
                <CategoryLink
                  label={wholesaler?.brandName}
                  href={`/store/${wholesaler?.slug}`}
                  key={`ui.menu-section-${hoveredNavLinkItem?.index}-wholesaler-${index + 1}`}
                />
              ))}
            </Box>
          </>
        )}
      </Grid2>

      <Grid2
        spacing="12px"
        direction="column"
        alignItems="start"
        justifyContent="start"
        size={{ xs: 12, md: 3 }}
        sx={{ padding: '8px 0' }}
        key={`ui.menu-section-${hoveredNavLinkItem?.index}-banners`}
      >
        {selectedMenu?.banners?.map((banner, index) => (
          <BannersImage
            href={banner.link.url}
            src={banner?.image?.url}
            alt={banner?.link?.label}
            key={`ui.menu-section-${hoveredNavLinkItem?.index}-banner-${index + 1}`}
          />
        ))}
      </Grid2>
    </Grid2>
  );
};

export default UIMenuSection;
